import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";

import http from "../../../helpers/http";
import { fetchPricing } from "../../../states/actions/fetchPricing";
import { fetchSignup } from "../../../states/actions/fetchSignup";
import LoadingScreen from "../../common/LoadingScreen";
import Seo from "../../common/Seo";
import ImageControl from "../../common/ImageControl";
import EventSignupForm from "./Form";
import { eventPlans } from "./eventPlans";

// Event signup: one page to pick a plan and create the account, then
// Stripe's hosted page for the payment (card, Apple Pay, Google Pay or bank),
// with the event's Promo Code applied. Reached from a QR code:
//   /event?code=XXXX            (optionally &plan=<plan_slug> to preselect)
// It calls the same endpoints as /signup and the checkout's payment link. If
// the Stripe page can't be opened, the attendee is sent to the normal checkout
// with the plan and code already applied (/checkout/:plan_id?code=XXXX).

const EventSignup = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const promoCode = (searchParams.get("code") || "").trim().toUpperCase();
  const planSlug = (searchParams.get("plan") || "").trim().toLowerCase();

  const authToken = useSelector((state) => state.fetchSignin.authToken);
  const memberEmail = useSelector(
    (state) => state.fetchSiteSettings.site_settings?.member?.mem_email
  );
  const pricing = useSelector((state) => state.fetchPricing.content);
  const isPricingLoading = useSelector((state) => state.fetchPricing.isLoading);
  const signupData = useSelector((state) => state.fetchSignup.content);
  const isSignupLoading = useSelector((state) => state.fetchSignup.isLoading);

  // The same Terms & Conditions and Subscription Agreement text the checkout
  // terms popup shows (both come from get_page() on the backend).
  const [terms, setTerms] = useState(null);

  useEffect(() => {
    dispatch(fetchPricing());
    if (!authToken) dispatch(fetchSignup());
    Promise.all([
      http.post("terms-conditions-page"),
      http.post("subscription-agreement-page"),
    ])
      .then(([termsRes, agreementRes]) => {
        const terms_conditions = termsRes?.data?.content;
        const subscription_agreement = agreementRes?.data?.content;
        // `failed` stops the form from asking anyone to accept blank terms.
        setTerms(
          terms_conditions?.policy_text && subscription_agreement?.policy_text
            ? { terms_conditions, subscription_agreement }
            : { failed: true }
        );
      })
      .catch(() => setTerms({ failed: true }));
  }, []);

  const isLoading =
    isPricingLoading || (!authToken && isSignupLoading) || terms === null;
  if (isLoading) return <LoadingScreen />;

  const { content, industries, primary_interests } = signupData || {};

  return (
    <>
      <Seo title="Start your free trial | Rhino Recon" noindex />
      <ToastContainer />
      <section className="logon signupLogon">
        <div className="contain">
          <div className="flex">
            <div className="colL">
              <div className="inner">
                <EventSignupForm
                  plans={eventPlans(pricing?.monthly_plans)}
                  planSlug={planSlug}
                  promoCode={promoCode}
                  isLoggedIn={!!authToken}
                  memberEmail={memberEmail}
                  industries={industries}
                  primary_interests={primary_interests}
                  terms={terms}
                />
              </div>
            </div>
          </div>
        </div>
        {content?.image1 && (
          <div className="lg_image">
            <ImageControl src={content.image1} folder="images" />
          </div>
        )}
      </section>
    </>
  );
};

export default EventSignup;
